import * as React from 'react';
import { useSelector } from '../../../utils/useSelector';
import { Kanban, Tabular } from '../../Icon/icon';
import './pageSetup.scss';
import { useActions } from '../viewModalActions';
import { ViewType } from '../../../enums/ViewType';
import { cn, findOneByProps } from '../../../utils';
import { ModalGroup, ModalGroups } from '../../ModalGroup/modalGroup';
import { AuditData } from '../../AuditData/auditData';
import { Toggler } from '../../Toggler/toggler';
import TooltipPopover from '../../Popover/popover';
import { TypeaheadChecklist } from '../../Typeahead/typeahead';

const Layouts = [
  { type: ViewType.Kanban, Icon: Kanban, name: 'Board' },
  { type: ViewType.Tabular, Icon: Tabular, name: 'Grid' }
];

export const PageSetup = () => {
  const name = useSelector(s => s.viewModal?.name);
  const viewType = useSelector(s => s.viewModal?.viewType);
  const description = useSelector(s => s.viewModal?.description);
  const isMyItemsView = useSelector(s => s.viewModal?.isMyItemsView);
  const parentView = useSelector(s => s.viewModal?.parentView);
  const isEfsBuilder = useSelector(s => s.view?.selected?.props?.isEFSBuilder);
  const { setViewSetting } = useActions();

  return (
    <ModalGroups>
      <ModalGroup>
        <div>
          <label htmlFor="viewName">View Name</label>
          <div className="input-group">
            <input
              id="viewName"
              type="text"
              className="form-control"
              disabled={isMyItemsView || !!parentView}
              value={name}
              onChange={e => {
                setViewSetting({ name: e.target.value });
              }}
            />
          </div>
        </div>
        <div>
          <label>Choose a Layout</label>
          <div className="view-layouts">
            {Layouts.map(({ type, Icon, name }) => (
              <div
                key={type}
                className={cn({
                  active: type == viewType,
                  disabled:
                    isMyItemsView ||
                    !!parentView ||
                    (type == ViewType.Tabular && isEfsBuilder)
                })}
                onClick={() => {
                  if (
                    !isMyItemsView &&
                    !parentView &&
                    !(type == ViewType.Tabular && isEfsBuilder)
                  ) {
                    setViewSetting({ viewType: type });
                  }
                }}
              >
                <span>
                  <Icon />
                </span>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor="viewDescription">Description</label>
          <div className="input-group">
            <textarea
              className="form-control"
              id="viewDescription"
              rows={3}
              maxLength={2000}
              value={description}
              disabled={!!parentView}
              onChange={e => {
                setViewSetting({ description: e.target.value });
              }}
            />
          </div>
        </div>
        <ViewInheritance />
        <AuditData dataType="view" />
      </ModalGroup>
    </ModalGroups>
  );
};

const ViewInheritance = () => {
  const showInWorkspaces = useSelector(s => s.viewModal?.showInWorkspaces);
  const childWorkspaces = useSelector(s =>
    s.planning?.selectedWorkspace?.childrenRefIds
      ?.map(refId => findOneByProps(s.planning.workspaces, { refId }))
      .filter(w => !!w?.props)
      .sort((a, b) => a.props.name.localeCompare(b.props.name))
  );
  const { setViewSetting } = useActions();
  if (!childWorkspaces?.length) return null;
  return (
    <>
      <div className="toggle-field tf-packed">
        <div>
          Show this view in child workspaces?{' '}
          <TooltipPopover
            pointerPosition="top"
            content="This will inherit the view to all child workspaces. Toggling off will hide the view for users in the child workspaces."
            type="info"
          />
        </div>
        <Toggler
          checked={!!showInWorkspaces}
          onChange={e =>
            setViewSetting({ showInWorkspaces: e ? ['All'] : null })
          }
        />
      </div>
      {!!showInWorkspaces && (
        <div className="toggle-field tf-packed">
          <div>Show in all child workspaces?</div>
          <Toggler
            checked={showInWorkspaces.includes('All')}
            onChange={e =>
              setViewSetting({ showInWorkspaces: e ? ['All'] : [] })
            }
          />
        </div>
      )}
      {!!showInWorkspaces && (
        <div className="typeahead-field">
          <label>Select Child Workspace</label>
          <TypeaheadChecklist
            options={childWorkspaces}
            selected={w => showInWorkspaces.includes(w.props.refId)}
            disabled={showInWorkspaces.includes('All')}
            inline={!showInWorkspaces.includes('All')}
            onSelect={w => {
              const newShowInWorkspaces = showInWorkspaces.filter(
                id => id != w.props.refId
              );
              if (newShowInWorkspaces.length == showInWorkspaces.length) {
                newShowInWorkspaces.push(w.props.refId);
              }
              setViewSetting({ showInWorkspaces: newShowInWorkspaces });
            }}
          />
        </div>
      )}
    </>
  );
};
